import { useTranslation } from 'react-i18next';
import EvelynIcon from 'components/atoms/icons/evelyn-monochrome.svg?react';
import BookmarkIcon from 'components/atoms/icons/bookmark-monochrome.svg?react';
import CommentIcon from 'components/atoms/icons/comment-monochrome.svg?react';
import CommentHyperPressure from 'components/atoms/icons/comment-hyper-pressure.svg?react';
import CommentHypoPressure from 'components/atoms/icons/comment-hypo-pressure.svg?react';
import CommentHyperGlucose from 'components/atoms/icons/comment-hyper-glucose.svg?react';
import CommentMaCritical from 'components/atoms/icons/comment-ma-critical.svg?react';
import CommentHypoGlucose from 'components/atoms/icons/comment-hypo-glucose.svg?react';
import CommentLowSpo2 from 'components/atoms/icons/comment-low-spo2';
import CommentTachycardia from 'components/atoms/icons/comment-tachycardia';
import CommentBradycardia from 'components/atoms/icons/comment-bradycardia.svg?react';
import CommentNoCall from 'components/atoms/icons/comment-no-call.svg?react';
import SendCommentIcon from 'components/atoms/icons/send-comment.svg?react';
import ChfWeightIcon from 'components/atoms/icons/comment-chf-weight-assessment';
import classNames from 'classnames';
import { useReducer, useRef, type FC, type ChangeEvent, useMemo } from 'react';
import BaseModal from 'components/atoms/base-modal';
import CommentBookmarkList from 'components/molecules/comment-bookmark/list';
import RpmCommentModal from 'components/templates/rpm/patients/patient/activity/modal';
import BaseIconButton from 'components/atoms/base-icon-button';
import { ReadingType, ReadingStatus } from 'infrastructure/enums';
import RpmAlertAssessmentModals from 'components/organisms/rpm-alert-assessment-modal';
import useUser from 'utils/useUser';
import CriticalNoCallModals from 'components/organisms/critical-no-call-template';
import { replaceWithBold } from 'infrastructure/functions/bold-digits';
import MaCriticalReadingTemplate from 'components/molecules/patient-reading-template/ma-critical-reading-template';
import NurseMaCriticalReadingModal from 'components/molecules/patient-reading-template/nurse-ma-critical-reading';
import { isCHFReading } from 'utils/get-reading-info';

import s from './styles.module.scss';
import { useCommentColumn } from './hooks/use-comment-column';
import { useOpenAssessmentModal } from './hooks/use-open-assessment-modal';

import type { Nullable } from 'infrastructure/types';
import type { ReadingStatus as ReadingStatusType } from 'infrastructure/enums';
import type {
  IAiCommentResponse,
  TAddCommentFn,
  TGenAiCommentFn,
} from 'infrastructure/hooks/rpm';
import type { IPatientThresholds } from 'infrastructure/interfaces';
import type {
  BloodOxygenStatus,
  TStatuses,
  TThresholdName,
  Value,
} from 'infrastructure/interfaces/i-rpm-patient-activity';

interface CommentColumnProps {
  addComment: TAddCommentFn;
  genAiComment: TGenAiCommentFn;
  deviceId: string;
  readingId: string;
  idx: number | string;
  createdAt: string;
  showAiButton: boolean;
  currentInteractionStatus?: boolean;
  patientGuid: string;
  genAiCommentLoading?: boolean;
  isCommentGenerationLimitReached: boolean;
  aiData?: IAiCommentResponse;
  isErroneous?: Nullable<boolean>;
  values: Value;
  statuses: TStatuses;
  thresholds: IPatientThresholds;
  readingType: ReadingType;
  globalStatus: ReadingStatusType;
  thresholdName?: Nullable<TThresholdName>;
  disabled?: boolean;
  patientRpmDoNotCall?: boolean;
  isRecovered?: boolean;
}

const CommentColumn: FC<CommentColumnProps> = ({
  addComment,
  genAiComment,
  deviceId,
  readingId,
  idx,
  createdAt,
  showAiButton,
  currentInteractionStatus,
  patientGuid,
  genAiCommentLoading,
  isCommentGenerationLimitReached,
  aiData,
  isErroneous,
  thresholds,
  values,
  statuses,
  readingType,
  globalStatus,
  thresholdName,
  disabled: disableForm,
  patientRpmDoNotCall,
  isRecovered,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const { t } = useTranslation();
  const {
    openBookmarkModal,
    openTemplateModal,
    templateValues,
    showBookmarkBtn,
    disabled: disabledComment,
    aiButtonTitle,
    updateAction,
    loading,
    message,
    submitCommentLockByAi,
    sendCommentButtonTitle,
    openPressureMinModal,
    openPressureModal,
    openCriticalNoCallModal,
    onSubmitComment,
    setTemplateValues,
    toggleOpenBookmark,
    toggleOpenTemplate,
    onApplyBookmark,
    toggleOpenPressure,
    toggleOpenPressureMin,
    checkReadingMinMaxType,
    isBloodPressureMinCritical,
    isBloodPressureMaxCritical,
    toggleOpenCriticalNoCall,
    isPulseMinCritical,
    isPulseMaxCritical,
    isSpo2MinCritical,
    getPulseCriticalType,
  } = useCommentColumn({
    addComment,
    deviceId,
    idx,
    readingId,
    globalStatus,
    currentInteractionStatus,
    patientGuid,
    aiData,
    isCommentGenerationLimitReached,
  });
  const { toggleAssessmentModal, isOpenAssessmentModalMap } =
    useOpenAssessmentModal();
  const [openNurseMaCritical, toggleNurseMaCritical] = useReducer(
    (v) => !v,
    false,
  );

  const disabled = disabledComment || isErroneous === true || disableForm;
  const { isPractice, isMedicalAssistantRole, isAgencyDoctor } = useUser();

  const bloodPressurePulsCriticalType = useMemo(() => {
    if (readingType === ReadingType.BloodPressure) {
      return getPulseCriticalType(values, thresholds);
    }
    return null;
  }, [readingType, values, thresholds]);

  const readingTypeText = useMemo(() => {
    if (readingType === ReadingType.BloodPressure) {
      const { pulse, oxygen } = statuses as BloodOxygenStatus;
      if (pulse === 'critical' && oxygen !== 'critical') {
        return ReadingType.Pulse;
      }
      return readingType;
    }
    return readingType;
  }, [readingType, values]);

  const readingCriticalType = checkReadingMinMaxType(
    readingType,
    globalStatus,
    values,
    thresholds,
  );

  const bloodPressureMaxCritical = isBloodPressureMaxCritical(
    globalStatus,
    values,
    thresholds,
  );

  const bloodPressureMinCritical = isBloodPressureMinCritical(
    globalStatus,
    values,
    thresholds,
  );

  const pulseMinCritical = isPulseMinCritical(values, thresholds);
  const pulseMaxCritical = isPulseMaxCritical(values, thresholds);
  const spo2MinCritical = isSpo2MinCritical(values, thresholds);
  const isCriticalWeight =
    readingType === ReadingType.Weight &&
    globalStatus === ReadingStatus.Critical;

  const isChf = isCHFReading(thresholdName);

  const isCriticalRedingType =
    readingCriticalType || bloodPressurePulsCriticalType || isCriticalWeight;

  const hideRpmCommentReading =
    readingType === ReadingType.BloodPressure ||
    readingType === ReadingType.SpO2 ||
    readingType === ReadingType.BloodGlucose ||
    readingType === ReadingType.Weight;

  // MAs don't use the RPM Comment template for Temperature, Resting Pulse
  // (Sleeping Mat variants) or Peak Flow readings — hide the icon for those.
  const isRestingPulse =
    readingType === ReadingType.SleepingMat ||
    readingType === ReadingType.SleepingMatDuration ||
    readingType === ReadingType.SleepingMatActivity;
  const hideCommentTemplateForMa =
    isMedicalAssistantRole &&
    (readingType === ReadingType.Temperature ||
      isRestingPulse ||
      readingType === ReadingType.PeakFlow);

  const isShowCommentIcon =
    !(isCriticalRedingType && hideRpmCommentReading) &&
    !hideCommentTemplateForMa;

  const disableAiButton =
    isCommentGenerationLimitReached || genAiCommentLoading || disabled;
  const disableBookmark = disabled || openBookmarkModal;
  const disableTemplate = disabled || openTemplateModal;
  const disableSubmit = disabled || submitCommentLockByAi;
  const isCriticalStatus = globalStatus === ReadingStatus.Critical;
  const isShowAiButton = showAiButton && !isPractice && !isCriticalStatus;
  const isShowSeparator =
    showBookmarkBtn || isShowCommentIcon || isShowAiButton;
  const disableForMARole =
    isCriticalStatus && isMedicalAssistantRole && !patientRpmDoNotCall;

  const hideActionButtons =
    isCriticalStatus && isAgencyDoctor && isMedicalAssistantRole;
  const showDoNotCallBtn =
    globalStatus === ReadingStatus.Critical && !hideActionButtons;
  const showBPMaxBtn =
    readingType === ReadingType.BloodPressure &&
    bloodPressureMaxCritical &&
    !hideActionButtons;
  const showBPMinBtn =
    readingType === ReadingType.BloodPressure &&
    bloodPressureMinCritical &&
    !hideActionButtons;
  const showGlucoseBtn =
    readingType === ReadingType.BloodGlucose &&
    readingCriticalType &&
    !hideActionButtons;
  const showSpo2Btn =
    readingType === ReadingType.SpO2 && spo2MinCritical && !hideActionButtons;
  const showPulseMaxBtn =
    pulseMaxCritical &&
    (bloodPressurePulsCriticalType || readingCriticalType) &&
    !hideActionButtons;
  const showPulseMinBtn =
    pulseMinCritical &&
    (bloodPressurePulsCriticalType || readingCriticalType) &&
    !hideActionButtons;
  const showWeightBtn = isCriticalWeight && !hideActionButtons;
  // Nurse-MA (HHA doctor userType + MA role) critical-reading guided template.
  // CHF weight = Fluid Management → no MA flow (routes to the nurse's own template),
  // so the button is hidden for a CHF weight reading; regular weight uses the review flow.
  const showNurseMaCriticalBtn =
    isCriticalStatus &&
    isAgencyDoctor &&
    isMedicalAssistantRole &&
    !(readingType === ReadingType.Weight && isChf);

  const formattedCommentMessage = message ? replaceWithBold(message) : '';

  const setCursorPlacement = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const input = e.target;
    const rawValue = input.value;
    const { selectionStart } = input;

    const inputData = (e.nativeEvent as InputEvent).data;

    if (inputData && /\d/.test(inputData)) {
      const bolded = replaceWithBold(rawValue);
      input.value = bolded;
      if (selectionStart < rawValue.length) {
        const newCursorPos = bolded.slice(0, selectionStart).length;
        input.setSelectionRange(newCursorPos, newCursorPos);
      }
    }
  };

  return (
    <>
      <div className={s.comment} data-cy="comment-input">
        <textarea
          name="message"
          placeholder={t('controls.typeYourComment')}
          value={formattedCommentMessage}
          onChange={(e) => {
            setCursorPlacement(e);
            updateAction({ idx, action: { message: e.target.value } });
          }}
          disabled={disabled}
          ref={textareaRef}
        />
        <div className={s['action-btns']}>
          <div>
            {isShowAiButton && (
              <BaseIconButton
                onClick={() =>
                  genAiComment({
                    deviceId,
                    readingId,
                  })
                }
                title={aiButtonTitle}
                disabled={disableForMARole || disableAiButton}
                loading={genAiCommentLoading}
                icon={
                  <EvelynIcon
                    width={24}
                    height={24}
                    className={s.icon}
                    data-disabled={genAiCommentLoading || disabled}
                  />
                }
              />
            )}
            {showBookmarkBtn && (
              <BaseIconButton
                onClick={toggleOpenBookmark}
                title={t('labels.bookmarkedComments')}
                disabled={disableForMARole || disableBookmark}
                icon={
                  <BookmarkIcon
                    width={26}
                    height={26}
                    className={classNames(s.icon, {
                      [s.opened]: openBookmarkModal,
                    })}
                    data-disabled={disabled}
                  />
                }
              />
            )}
            {isShowCommentIcon && (
              <BaseIconButton
                onClick={toggleOpenTemplate}
                title={
                  openTemplateModal
                    ? t('controls.onlyUseButtonOncePerComment')
                    : t('controls.openTemplateModal', { type: '' })
                }
                disabled={disableForMARole || disableTemplate}
                icon={
                  <CommentIcon
                    width={24}
                    height={24}
                    className={classNames(s.icon, {
                      [s.opened]: openTemplateModal,
                    })}
                    data-disabled={disabled}
                  />
                }
              />
            )}
          </div>
          {isShowSeparator && <div className={s.separator} />}
          <div>
            {showNurseMaCriticalBtn && (
              <BaseIconButton
                onClick={toggleNurseMaCritical}
                title={t('controls.openTemplateModal', {
                  type: 'Critical Reading Review',
                })}
                disabled={disabled || openNurseMaCritical}
                icon={
                  <CommentMaCritical
                    width={25}
                    height={25}
                    className={classNames(s.icon, {
                      [s.opened]: openNurseMaCritical,
                    })}
                    data-disabled={disabled}
                  />
                }
              />
            )}
            {showDoNotCallBtn && (
              <BaseIconButton
                onClick={toggleOpenCriticalNoCall}
                title={t('controls.openTemplateModal', {
                  type: 'Critical No Call',
                })}
                disabled={disableForMARole || disabled || openTemplateModal}
                icon={
                  <CommentNoCall
                    width={21}
                    height={21}
                    className={classNames(s.icon, {
                      [s.opened]: openCriticalNoCallModal,
                    })}
                    data-disabled={disabled}
                  />
                }
              />
            )}
            {showBPMaxBtn && (
              <BaseIconButton
                onClick={toggleOpenPressure}
                title={t('controls.openTemplateModal', {
                  type: 'Hypertension',
                })}
                disabled={disableForMARole || disabled || openPressureModal}
                icon={
                  <CommentHyperPressure
                    width={21}
                    height={21}
                    className={classNames(s.icon, {
                      [s.opened]: openPressureModal,
                    })}
                    data-disabled={disabled}
                  />
                }
              />
            )}
            {showBPMinBtn && (
              <BaseIconButton
                onClick={toggleOpenPressureMin}
                title={t('controls.openTemplateModal', {
                  type: 'Hypotension',
                })}
                disabled={disableForMARole || disabled || openPressureMinModal}
                icon={
                  <CommentHypoPressure
                    width={21}
                    height={21}
                    className={classNames(s.icon, {
                      [s.opened]: openPressureMinModal,
                    })}
                    data-disabled={disabled}
                  />
                }
              />
            )}
            {showGlucoseBtn && (
              <BaseIconButton
                onClick={() => toggleAssessmentModal('glucose')}
                title={t('controls.openTemplateModal', { type: 'Glucose' })}
                disabled={
                  disableForMARole ||
                  disabled ||
                  isOpenAssessmentModalMap.glucose
                }
                icon={
                  readingCriticalType === 'max' ? (
                    <CommentHyperGlucose
                      width={22}
                      height={22}
                      className={s.icon}
                      data-disabled={disabled}
                    />
                  ) : (
                    <CommentHypoGlucose
                      width={22}
                      height={22}
                      className={s.icon}
                      data-disabled={disabled}
                    />
                  )
                }
              />
            )}
            {showSpo2Btn && (
              <BaseIconButton
                onClick={() => toggleAssessmentModal('spo2')}
                title={t('controls.openTemplateModal', { type: 'SpO2' })}
                disabled={
                  disableForMARole || disabled || isOpenAssessmentModalMap.spo2
                }
                icon={
                  <CommentLowSpo2
                    width={21}
                    height={21}
                    className={classNames(s.icon, {
                      [s.opened]: isOpenAssessmentModalMap.spo2,
                    })}
                    data-disabled={disabled}
                  />
                }
              />
            )}
            {showPulseMaxBtn && (
              <BaseIconButton
                onClick={() => toggleAssessmentModal('pulse')}
                title={t('controls.openTemplateModal', {
                  type: 'Tachycardia',
                })}
                disabled={
                  disableForMARole || disabled || isOpenAssessmentModalMap.pulse
                }
                icon={
                  <CommentTachycardia
                    width={20}
                    height={20}
                    className={classNames(s.icon, {
                      [s.opened]: isOpenAssessmentModalMap.pulse,
                    })}
                    data-disabled={disabled}
                  />
                }
              />
            )}
            {showPulseMinBtn && (
              <BaseIconButton
                onClick={() => toggleAssessmentModal('pulse')}
                title={t('controls.openTemplateModal', {
                  type: 'Bradycardia',
                })}
                disabled={
                  disableForMARole || disabled || isOpenAssessmentModalMap.pulse
                }
                icon={
                  <CommentBradycardia
                    width={20}
                    height={20}
                    className={classNames(s.icon, {
                      [s.opened]: isOpenAssessmentModalMap.pulse,
                    })}
                    data-disabled={disabled}
                  />
                }
              />
            )}
            {showWeightBtn && (
              <BaseIconButton
                onClick={() => {
                  isChf
                    ? toggleAssessmentModal('chfWeight')
                    : toggleAssessmentModal('weight');
                }}
                title={t('controls.openTemplateModal', {
                  type: 'Weight',
                })}
                disabled={
                  disabled ||
                  (isChf
                    ? disableForMARole || isOpenAssessmentModalMap.chfWeight
                    : disableForMARole || isOpenAssessmentModalMap.weight)
                }
                icon={
                  <ChfWeightIcon
                    width={21}
                    height={21}
                    className={classNames(s.icon, {
                      [s.opened]:
                        isOpenAssessmentModalMap.chfWeight ||
                        isOpenAssessmentModalMap.weight,
                    })}
                    data-disabled={disabled}
                  />
                }
              />
            )}
          </div>
          <BaseIconButton
            dataCy="submit-reading-comment-button"
            className={s['send-comment-btn']}
            onClick={onSubmitComment}
            title={sendCommentButtonTitle}
            disabled={disableSubmit}
            loading={loading}
            icon={
              <SendCommentIcon
                width={24}
                height={24}
                className={classNames(s.icon, s['send-icon'], {
                  [s.disabled]: disabled,
                })}
                data-disabled={disabled}
              />
            }
          />
        </div>
      </div>
      {openBookmarkModal && (
        <BaseModal
          open={openBookmarkModal}
          onCancel={toggleOpenBookmark}
          width="1000px"
          title="Bookmarked comments"
        >
          <CommentBookmarkList onApply={onApplyBookmark} />
        </BaseModal>
      )}
      {openTemplateModal && (
        <RpmCommentModal
          isOpen={openTemplateModal}
          closeModal={toggleOpenTemplate}
          activityProps={{
            activity: {
              id: readingId,
              createdAt,
            },
            setValues: setTemplateValues,
            values: templateValues,
            handleSubmit: (msg: string) => {
              // Another source replaced the message: drop any Nurse-MA template answers.
              updateAction({
                idx,
                action: { message: msg, criticalReadingTemplate: undefined },
              });
            },
          }}
        />
      )}

      <RpmAlertAssessmentModals
        readingId={readingId}
        createdAt={createdAt}
        idx={idx}
        patientGuid={patientGuid}
        values={values}
        readingCriticalType={readingCriticalType}
        bloodPressurePulsCriticalType={bloodPressurePulsCriticalType}
        openPressureModal={openPressureModal}
        openPressureMinModal={openPressureMinModal}
        pulseMaxCritical={pulseMaxCritical}
        toggleOpenPressure={toggleOpenPressure}
        toggleOpenPressureMin={toggleOpenPressureMin}
        toggleAssessmentModal={toggleAssessmentModal}
        isOpenAssessmentModalMap={isOpenAssessmentModalMap}
      />

      <CriticalNoCallModals
        idx={idx}
        readingType={readingTypeText}
        openCriticalNoCallModal={openCriticalNoCallModal}
        toggleOpenCriticalNoCall={toggleOpenCriticalNoCall}
      />
      {isOpenAssessmentModalMap.maCritical && (
        <MaCriticalReadingTemplate
          toggle={() => toggleAssessmentModal('maCritical')}
          idx={idx}
        />
      )}
      {openNurseMaCritical && (
        <NurseMaCriticalReadingModal
          idx={idx}
          patientGuid={patientGuid}
          readingType={readingType}
          values={values}
          statuses={statuses}
          thresholds={thresholds}
          isRecovered={isRecovered}
          onClose={toggleNurseMaCritical}
        />
      )}
    </>
  );
};
export default CommentColumn;
